import React from 'react';
import {
  FileText,
  Users,
  MessageSquare,
  FileType,
  BarChart3,
  Clock,
  Rocket,
  Package,
  Tag,
  Info,
  Target,
  Calendar,
  DollarSign,
  TrendingUp,
  Layout,
  Palette,
  Settings,
  Globe,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Award,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

const ReportAnythingReport = ({response}:any) => {
  // Icon mapping based on key names
  const iconMap: Record<string, any> = {
  topic: FileText,
  deliverable: BarChart3,
  deliverables: Package,
  audience: Users,
  tone: MessageSquare,
  style: Palette,
  format: FileType,
  depth: TrendingUp,
  length: Layout,
  delivery: Clock,
  timeline: Calendar,
  campaign: Rocket,
  target: Target,
  budget: DollarSign,
  revenue: DollarSign,

  // 🔥 NEW ADDITIONS
  decision_support: Target,
  goals: Award,
  geographic_scope: Globe,
  boundaries: Package,
  forecast_ranges: TrendingUp,
  key_questions: AlertCircle,
  objective: AlertCircle,

  email: Mail,
  phone: Phone,
  location: MapPin,
  address: MapPin,
  role: Briefcase,
  title: Award,
  status: CheckCircle,
  website: Globe,
  settings: Settings,
  tags: Tag,
  default: Info,
};



  // Color mapping based on key names
  const colorMap: Record<string, { color: string; bgColor: string }> = {
  topic: { color: 'text-blue-600', bgColor: 'bg-blue-50' },
  objective: { color: 'text-blue-600', bgColor: 'bg-blue-50' },

  decision_support: { color: 'text-red-600', bgColor: 'bg-red-50' },
  goals: { color: 'text-green-600', bgColor: 'bg-green-50' },
  geographic_scope: { color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  boundaries: { color: 'text-purple-600', bgColor: 'bg-purple-50' },
  forecast_ranges: { color: 'text-teal-600', bgColor: 'bg-teal-50' },
  key_questions: { color: 'text-orange-600', bgColor: 'bg-orange-50' },

  deliverable: { color: 'text-purple-600', bgColor: 'bg-purple-50' },
  deliverables: { color: 'text-purple-600', bgColor: 'bg-purple-50' },
  audience: { color: 'text-green-600', bgColor: 'bg-green-50' },
  tone: { color: 'text-orange-600', bgColor: 'bg-orange-50' },
  style: { color: 'text-orange-600', bgColor: 'bg-orange-50' },
  format: { color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  depth: { color: 'text-pink-600', bgColor: 'bg-pink-50' },
  length: { color: 'text-pink-600', bgColor: 'bg-pink-50' },
  delivery: { color: 'text-teal-600', bgColor: 'bg-teal-50' },
  timeline: { color: 'text-teal-600', bgColor: 'bg-teal-50' },
  campaign: { color: 'text-red-600', bgColor: 'bg-red-50' },
  default: { color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

  // Convert camelCase or snake_case to Title Case
  const formatLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Get icon for a key
  const getIcon = (key: string) => {
    const lowerKey = key.toLowerCase();
    return iconMap[lowerKey] || iconMap.default;
  };

  // Get colors for a key
  const getColors = (key: string) => {
    const lowerKey = key.toLowerCase();
    return colorMap[lowerKey] || colorMap.default;
  };

  // Check if value should be displayed
  const isValidValue = (value: any): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && (value.trim() === '' || value.toLowerCase() === 'n/a')) return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && Object.keys(value).length === 0) return false;
    return true;
  };

  // Format value for display
  const formatValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  // Convert data object to array of items
  const summaryItems = Object.entries(response).map(([key, value]:any) => ({
    key,
    label: formatLabel(key),
    value: value,
    formattedValue: formatValue(value),
    icon: getIcon(key),
    colors: getColors(key),
    isValid: isValidValue(value),
  }));

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto pn_scroller">
      {/* Header */}
      <div className=" rounded-t-2xl p-3 text-gray-900 border-b border-gray-300">
       
        <p className=" text-md">Report Summary</p>
      </div>

      {/* Content */}
      <div className="bg-white rounded-b-2xl shadow-lg p-2">
        <div className="grid grid-cols-1 gap-2">
          {summaryItems.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className={`flex items-start gap-4 p-2 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                  !item.isValid
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 ${item.colors.bgColor} p-2 rounded-lg`}>
                  <Icon className={`w-3 h-3 ${item.colors.color}`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {item.label}
                  </p>
                  <p
                    className={`text-sm font-medium leading-relaxed break-words ${
                      !item.isValid ? 'text-gray-400 italic' : 'text-gray-900'
                    }`}
                  >
                    {!item.isValid ? 'Not specified' : item.formattedValue}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default ReportAnythingReport;